/* ============================================================
   ISJ Cart Drawer â€” Liquid-rendered version (optimized)
   - HTML rendered server-side in Liquid (zero flash)
   - Single round-trip add/remove via Section Rendering API
   - Optimistic UI on remove (instant disappear)
   ============================================================ */
(function() {
  'use strict';

  const SECTION_ID = 'isj-cart-drawer';

  let drawerEl = null;
  let isAdding = false;
  let pendingRequest = null;

  // ---------- DOM helpers ----------
  function getDrawer() {
    if (!drawerEl) drawerEl = document.getElementById('isj-cart-drawer');
    return drawerEl;
  }

  // ---------- Open / Close ----------
  function openDrawer() {
    const d = getDrawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('isj-cart-drawer-open');
    suppressTurbo();
  }

  function closeDrawer() {
    const d = getDrawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('isj-cart-drawer-open');
  }

  function isOpen() {
    const d = getDrawer();
    return d && d.getAttribute('aria-hidden') === 'false';
  }

  // ---------- Swap drawer HTML from a section response ----------
  function swapDrawerHTML(sectionHTML) {
    if (!sectionHTML) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = sectionHTML;
    const newDrawer = tmp.querySelector('#isj-cart-drawer');
    const currentDrawer = getDrawer();
    if (newDrawer && currentDrawer) {
      const newInner = newDrawer.querySelector('.isj-cart-drawer__inner');
      const currentInner = currentDrawer.querySelector('.isj-cart-drawer__inner');
      if (newInner && currentInner) {
        currentInner.innerHTML = newInner.innerHTML;
        // Carry the server-computed "expired gifts" signal from the fresh root onto the live root,
        // then act on it. The root attribute lives outside .__inner, so it must be copied explicitly.
        currentDrawer.setAttribute('data-isj-expired-gifts', newDrawer.getAttribute('data-isj-expired-gifts') || '');
        bindHandlers();
        updateHeaderCountFromDOM();
        removeExpiredGifts();
      }
    }
  }

  // Render-driven free-gift cleanup: the section stamps `data-isj-expired-gifts` with the line keys of
  // any claimed gift whose tier threshold is no longer met (qualifying = non-gift pre-discount subtotal).
  // Removing them here — on every render — is reliable, unlike a timing-based sweep. Removing a gift never
  // changes the qualifying total, so a single batched pass converges (no loop).
  var __isjRemovingExpired = false;
  async function removeExpiredGifts() {
    if (__isjRemovingExpired) return;
    const d = getDrawer();
    if (!d) return;
    const keys = (d.getAttribute('data-isj-expired-gifts') || '').split('|||').filter(Boolean);
    if (!keys.length) return;
    __isjRemovingExpired = true;
    d.setAttribute('data-isj-expired-gifts', ''); // clear so the re-render swap doesn't re-trigger
    try {
      let lastResp;
      for (let i = 0; i < keys.length; i++) {
        const isLast = (i === keys.length - 1);
        const url = '/cart/change.js' + (isLast ? '?sections=' + SECTION_ID : '');
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: keys[i], quantity: 0 })
        });
        lastResp = await r.json();
      }
      __isjRemovingExpired = false; // release before the swap so the post-render check can run if needed
      if (lastResp && lastResp.sections && lastResp.sections[SECTION_ID]) {
        swapDrawerHTML(lastResp.sections[SECTION_ID]);
      }
    } catch (e) {
      console.error('[ISJ Drawer] removeExpiredGifts failed', e);
      __isjRemovingExpired = false;
    }
  }

  // Extract item count from the freshly-rendered drawer HTML and update header
  function updateHeaderCountFromDOM() {
    const d = getDrawer();
    if (!d) return;
    const countEl = d.querySelector('.isj-cart-drawer__count');
    if (!countEl) {
      // Empty state â€” header count = 0
      document.querySelectorAll('.cart_count').forEach(el => { el.textContent = ''; });
      return;
    }
    const m = countEl.textContent.match(/\d+/);
    const count = m ? parseInt(m[0], 10) : 0;
    document.querySelectorAll('.cart_count').forEach(el => {
      el.textContent = count > 0 ? count : '';
    });
  }

  // ---------- Cart line operations (single round-trip with sections) ----------
  async function updateCartLine(key, qty) {
    try {
      // Build the op list: parent change + linked-line trims (gift packaging AND engraving)
      const ops = [{ key: key, qty: qty }];
      // Source 1 (authoritative): DOM data attributes set by the snippet at render time —
      // these reflect what the cart *displayed* as linked, regardless of whether the
      // _for_line_key value still happens to match Shopify's current line key.
      const linkedKeySet = new Set();
      try {
        const drawerEl = getDrawer();
        if (drawerEl) {
          const itemEl = drawerEl.querySelector('[data-isj-line-item][data-line-key="' + key + '"]');
          if (itemEl) {
            const giftAttr = itemEl.getAttribute('data-linked-gift-keys') || '';
            const engravAttr = itemEl.getAttribute('data-linked-engraving-keys') || '';
            giftAttr.split('|||').forEach(k => { if (k) linkedKeySet.add(k); });
            engravAttr.split('|||').forEach(k => { if (k) linkedKeySet.add(k); });
          }
        }
      } catch (e) { /* fall through to cart-based scan */ }
      // Source 2 (fallback / extra coverage): scan cart for any item whose _for_line_key matches.
      // Only needed when the line actually has DOM-detected linked sub-items (gift packaging, etc.).
      // Skipping it for plain lines removes a redundant /cart.js round-trip and makes qty changes snappy.
      if (linkedKeySet.size > 0) {
        try {
          const cart = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(r => r.json());
          for (const it of (cart.items || [])) {
            const fk = it.properties && (it.properties._for_line_key || it.properties['_for_line_key']);
            const isLinked = linkedKeySet.has(it.key) || fk === key;
            if (isLinked && it.quantity > qty) {
              // Clamp linked quantity to new parent quantity (0 if parent removed)
              ops.push({ key: it.key, qty: qty });
            }
          }
        } catch (e) { /* if cart fetch fails, just do the single parent op */ }
      }

      // Execute ops sequentially; only the LAST request requests sections HTML
      let lastResp;
      for (let i = 0; i < ops.length; i++) {
        const isLast = (i === ops.length - 1);
        const url = '/cart/change.js' + (isLast ? '?sections=' + SECTION_ID : '');
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: ops[i].key, quantity: ops[i].qty })
        });
        lastResp = await r.json();
      }
      if (lastResp && lastResp.sections && lastResp.sections[SECTION_ID]) {
        swapDrawerHTML(lastResp.sections[SECTION_ID]);
      }
      // After ANY quantity change, sweep for items that must not survive: orphaned kit components
      // (a removed kit diffuser leaves its chosen fragrance behind) AND claimed free gifts whose
      // qualifying subtotal has now dropped below their tier threshold. Lowering a quantity (not just
      // removing) can un-qualify a gift, so this runs on every update, not only on qty === 0.
      setTimeout(reconcileOrphanGifts, 120);
    } catch (e) {
      console.error('[ISJ Drawer] updateCartLine failed', e);
    }
  }

  // Remove ONE gift-packaging unit (single line key) — decrements its qty by 1, or removes it if qty was 1
  async function removeOneLinkedGift(lineKey) {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(r => r.json());
      const item = (cart.items || []).find(it => it.key === lineKey);
      if (!item) return;
      const newQty = Math.max(0, (item.quantity || 0) - 1);
      const r = await fetch('/cart/change.js?sections=' + SECTION_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity: newQty })
      });
      const data = await r.json();
      if (data && data.sections && data.sections[SECTION_ID]) {
        swapDrawerHTML(data.sections[SECTION_ID]);
      }
    } catch (e) {
      console.error('[ISJ Drawer] removeOneLinkedGift failed', e);
    }
  }

  // Auto-cleanup: remove GIFT PACKAGING orphans only (whose parent line key is gone).
  // Engraving is NOT included — the engraving app's _for_line_key value isn't always a real
  // Shopify line key, so a "no match" doesn't reliably mean orphan. We render orphan engraving
  // in the main cart loop instead so the customer sees the $9 charge and can manage it manually.
  async function reconcileOrphanGifts() {
    if (window.__isjReconcilingOrphans) return;
    window.__isjReconcilingOrphans = true;
    try {
      const drawerEl = getDrawer();
      if (!drawerEl) return;
      const cart = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(r => r.json());
      if (!cart.items || !cart.items.length) return;
      const validKeys = new Set(cart.items.map(it => it.key));
      const orphanKeys = [];

      // (a) Gift-packaging orphans (by _for_line_key) — only when a gift product is configured
      const giftHandle = drawerEl.getAttribute('data-gift-product-handle') || '';
      if (giftHandle) {
        for (const it of cart.items) {
          if (it.handle !== giftHandle) continue;
          const fk = it.properties && (it.properties._for_line_key || it.properties['_for_line_key']);
          if (fk && !validKeys.has(fk)) orphanKeys.push(it.key);
        }
      }

      // (b) BUILD YOUR KIT component orphans (by _kit_group): a non-diffuser line whose kit group
      //     no longer has a diffuser parent (the kit diffuser was removed) — these must be auto-removed
      //     so the customer is never charged for a leftover ("sticky item").
      const validKitGroups = new Set();
      for (const it of cart.items) {
        const kg = it.properties && it.properties._kit_group;
        const pt = (it.product_type || '').toLowerCase();
        if (kg && pt.indexOf('diffuser') !== -1) validKitGroups.add(kg);
      }
      for (const it of cart.items) {
        const kg = it.properties && it.properties._kit_group;
        const pt = (it.product_type || '').toLowerCase();
        if (kg && pt.indexOf('diffuser') === -1 && !validKitGroups.has(kg)) {
          if (orphanKeys.indexOf(it.key) === -1) orphanKeys.push(it.key);
        }
      }

      // (c) Tier free-gift threshold cleanup is handled render-driven in removeExpiredGifts()
      //     (the section stamps the expired keys), so it isn't repeated here.

      if (!orphanKeys.length) return;
      let lastResp;
      for (let i = 0; i < orphanKeys.length; i++) {
        const isLast = (i === orphanKeys.length - 1);
        const url = '/cart/change.js' + (isLast ? '?sections=' + SECTION_ID : '');
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: orphanKeys[i], quantity: 0 })
        });
        lastResp = await r.json();
      }
      if (lastResp && lastResp.sections && lastResp.sections[SECTION_ID]) {
        swapDrawerHTML(lastResp.sections[SECTION_ID]);
      }
    } catch (e) {
      console.error('[ISJ Drawer] reconcileOrphanGifts failed', e);
    } finally {
      window.__isjReconcilingOrphans = false;
    }
  }

  // Remove ALL linked gift-packaging lines for a given parent line key
  async function removeAllLinkedGifts(parentKey) {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(r => r.json());
      const giftKeys = (cart.items || [])
        .filter(it => {
          const fk = it.properties && (it.properties._for_line_key || it.properties['_for_line_key']);
          return fk === parentKey;
        })
        .map(it => it.key);
      if (!giftKeys.length) return;
      let lastResp;
      for (let i = 0; i < giftKeys.length; i++) {
        const isLast = (i === giftKeys.length - 1);
        const url = '/cart/change.js' + (isLast ? '?sections=' + SECTION_ID : '');
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: giftKeys[i], quantity: 0 })
        });
        lastResp = await r.json();
      }
      if (lastResp && lastResp.sections && lastResp.sections[SECTION_ID]) {
        swapDrawerHTML(lastResp.sections[SECTION_ID]);
      }
    } catch (e) {
      console.error('[ISJ Drawer] removeAllLinkedGifts failed', e);
    }
  }

  // Optimistic remove â€” hide item visually, sync with server in background
  function optimisticRemove(itemEl, key) {
    if (!itemEl) {
      updateCartLine(key, 0);
      return;
    }
    // Fade + collapse animation
    itemEl.style.transition = 'opacity 0.15s ease, max-height 0.2s ease, margin 0.2s ease, padding 0.2s ease';
    itemEl.style.overflow = 'hidden';
    itemEl.style.maxHeight = itemEl.offsetHeight + 'px';
    requestAnimationFrame(() => {
      itemEl.style.opacity = '0';
      itemEl.style.maxHeight = '0';
      itemEl.style.marginBottom = '0';
      itemEl.style.marginTop = '0';
      itemEl.style.paddingTop = '0';
      itemEl.style.paddingBottom = '0';
      itemEl.style.borderBottom = '0';
    });
    // Fire actual remove in background â€” section HTML swap happens when response arrives
    updateCartLine(key, 0);
  }

  // ---------- Event handlers ----------
  function bindHandlers() {
    const d = getDrawer();
    if (!d) return;

    d.querySelectorAll('[data-isj-close]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        closeDrawer();
      });
    });

    d.querySelectorAll('[data-isj-checkout]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        window.location.href = '/checkout';
      });
    });

    d.querySelectorAll('[data-isj-qty-dir]').forEach(btn => {
      btn.addEventListener('click', async e => {
        e.preventDefault();
        if (btn.disabled) return;
        const wrap = btn.closest('[data-isj-qty-line]');
        if (!wrap) return;
        const key = wrap.getAttribute('data-isj-qty-line');
        const dir = parseInt(btn.getAttribute('data-isj-qty-dir'), 10);
        const curr = parseInt(wrap.getAttribute('data-isj-qty-current'), 10) || 1;
        const next = Math.max(1, curr + dir);
        if (next === curr) return;
        wrap.setAttribute('data-isj-qty-current', String(next));
        const valEl = wrap.querySelector('.isj-cart-drawer__qty-value');
        if (valEl) valEl.textContent = String(next);
        const dec = wrap.querySelector('[data-isj-qty-dir="-1"]');
        if (dec) dec.disabled = next <= 1;
        await updateCartLine(key, next);
      });
    });

    d.querySelectorAll('[data-isj-remove]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        const key = el.getAttribute('data-isj-remove');
        const itemEl = el.closest('.isj-cart-drawer__item');
        optimisticRemove(itemEl, key);
      });
    });

    // Remove ONE linked gift packaging unit (single message row)
    d.querySelectorAll('[data-isj-remove-one-gift]').forEach(el => {
      el.addEventListener('click', async e => {
        e.preventDefault();
        e.stopPropagation();
        const lineKey = el.getAttribute('data-isj-remove-one-gift');
        // Visual: fade the row
        const rowEl = el.closest('.isj-cart-drawer__nested-gift-msg-row');
        if (rowEl) {
          rowEl.style.transition = 'opacity 0.15s ease, max-height 0.2s ease, margin 0.2s ease';
          rowEl.style.overflow = 'hidden';
          rowEl.style.maxHeight = rowEl.offsetHeight + 'px';
          requestAnimationFrame(() => {
            rowEl.style.opacity = '0';
            rowEl.style.maxHeight = '0';
            rowEl.style.marginTop = '0';
          });
        }
        await removeOneLinkedGift(lineKey);
      });
    });

    // Remove all linked gift packaging for a parent line item
    d.querySelectorAll('[data-isj-remove-gifts]').forEach(el => {
      el.addEventListener('click', async e => {
        e.preventDefault();
        const parentKey = el.getAttribute('data-isj-remove-gifts');
        // Visual: fade the nested gift block
        const giftBlock = el.closest('.isj-cart-drawer__nested-gift');
        if (giftBlock) {
          giftBlock.style.transition = 'opacity 0.15s ease, max-height 0.2s ease, margin 0.2s ease, padding 0.2s ease';
          giftBlock.style.overflow = 'hidden';
          giftBlock.style.maxHeight = giftBlock.offsetHeight + 'px';
          requestAnimationFrame(() => {
            giftBlock.style.opacity = '0';
            giftBlock.style.maxHeight = '0';
            giftBlock.style.marginTop = '0';
            giftBlock.style.paddingTop = '0';
            giftBlock.style.paddingBottom = '0';
          });
        }
        await removeAllLinkedGifts(parentKey);
      });
    });

    // ---------- Phase 3: CLAIM tier gift ----------
    d.querySelectorAll('[data-isj-claim]').forEach(el => {
      el.addEventListener('click', async e => {
        e.preventDefault();
        const variantId = el.getAttribute('data-variant-id');
        if (!variantId) {
          console.error('[ISJ Drawer] CLAIM: no variant ID');
          return;
        }
        // Disable button to prevent double-click
        if (el.disabled) return;
        el.disabled = true;
        el.style.opacity = '0.7';
        const originalText = el.textContent;
        el.textContent = 'ADDING...';
        try {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', 1);
          formData.append('sections', SECTION_ID);
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: formData
          });
          const data = await r.json();
          if (data && data.sections && data.sections[SECTION_ID]) {
            swapDrawerHTML(data.sections[SECTION_ID]);
          } else {
            // Fallback: refresh via section endpoint
            const r2 = await fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' });
            const data2 = await r2.json();
            if (data2[SECTION_ID]) swapDrawerHTML(data2[SECTION_ID]);
          }
        } catch (err) {
          console.error('[ISJ Drawer] CLAIM failed', err);
          el.disabled = false;
          el.style.opacity = '1';
          el.textContent = originalText;
        }
      });
    });
  }

  // ---------- Suppress Turbo's old drawer ----------
  function suppressTurbo() {
    const sels = ['.cart-show-drawer','.cart_content','.cart__contents','.cart__contents-inner','.cart__items','.js-cart-content__form','.pplr_cart_form_n','.mini-cart-content','.dropdown_container.mini-cart','.cart-popup','.mini-cart-wrapper','.drawer-overlay.active'];
    document.querySelectorAll(sels.join(',')).forEach(el => {
      if (el.id === 'isj-cart-drawer' || el.closest('#isj-cart-drawer')) return;
      el.style.cssText = 'display:none !important; visibility:hidden !important;';
      el.classList.remove('open','is-open','active','visible','show','animated','fadeIn');
    });
    if (window.location.search.indexOf('viewcart=true') !== -1) {
      const cleanUrl = window.location.pathname + window.location.search.replace(/[?&]viewcart=true/g, '').replace(/^&/, '?');
      window.history.replaceState({}, '', cleanUrl);
    }
    document.body.classList.remove('no-scroll', 'cart-open', 'mini-cart-open');
  }

  // ---------- Subscription frequency selector (one-time <-> selling plan) ----------
  async function changeSellingPlan(key, planId, qty) {
    try {
      const body = { id: key, quantity: qty || 1 };
      body.selling_plan = planId ? planId : null; // null = switch back to one-time purchase
      const r = await fetch('/cart/change.js?sections=' + SECTION_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
      const data = await r.json();
      if (data && data.sections && data.sections[SECTION_ID]) {
        swapDrawerHTML(data.sections[SECTION_ID]);
      } else {
        const r2 = await fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' });
        const d2 = await r2.json();
        if (d2 && d2[SECTION_ID]) swapDrawerHTML(d2[SECTION_ID]);
      }
    } catch (e) {
      console.error('[ISJ Drawer] changeSellingPlan failed', e);
    }
  }
  function closeAllSubDropdowns(except) {
    document.querySelectorAll('.isj-sub-dd.is-open').forEach(dd => {
      if (dd === except) return;
      dd.classList.remove('is-open');
      const btn = dd.querySelector('[data-isj-sub-toggle]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }
  document.addEventListener('click', e => {
    const t = e.target;
    if (!t || !t.closest) return;

    // 1) toggle button
    const toggle = t.closest('[data-isj-sub-toggle]');
    if (toggle) {
      e.preventDefault();
      const dd = toggle.closest('.isj-sub-dd');
      const willOpen = !dd.classList.contains('is-open');
      closeAllSubDropdowns(dd);
      dd.classList.toggle('is-open', willOpen);
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      return;
    }

    // 2) option selected
    const opt = t.closest('[data-isj-sub-opt]');
    if (opt) {
      e.preventDefault();
      const wrap = opt.closest('[data-isj-sub]');
      const dd = opt.closest('.isj-sub-dd');
      if (dd) dd.classList.remove('is-open');
      if (!wrap) return;
      const key = wrap.getAttribute('data-line-key');
      const qty = parseInt(wrap.getAttribute('data-line-qty'), 10) || 1;
      const planId = opt.getAttribute('data-isj-sub-opt'); // '' => one-time
      changeSellingPlan(key, planId, qty);
      return;
    }

    // 3) outside click closes any open dropdown
    if (!t.closest('.isj-sub-dd')) closeAllSubDropdowns(null);
  });

  // ---------- Cart icon click hook ----------
  const CART_ICON_SELECTORS = '.cart-drawer-icon,.mini_cart,a[href="/cart"],a[href$="/cart"],.cart-show-drawer,[data-cart-toggle],.site-header__cart,.site-header__cart-link,.cart-link,.header__cart-icon';
  document.addEventListener('click', e => {
    const t = e.target.closest(CART_ICON_SELECTORS);
    if (t && (!getDrawer() || !getDrawer().contains(t))) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      openDrawer();
    }
  }, true);

  // ---------- Add-to-cart success: open + show new item from same response ----------
  async function handleCartAddSuccess() {
    if (isAdding) return;
    isAdding = true;
    try {
      // Single round-trip: get section HTML in one call
      const r = await fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' });
      const data = await r.json();
      const html = data[SECTION_ID];
      if (html) {
        swapDrawerHTML(html);
      }
      if (!isOpen()) openDrawer();
    } catch (e) {
      console.error('[ISJ Drawer] handleCartAddSuccess failed', e);
    }
    isAdding = false;
  }

  // ---------- Add-to-cart form submit (intercept + use sections param) ----------
  document.addEventListener('submit', e => {
    const form = e.target;
    if (form && form.action && form.action.indexOf('/cart/add') !== -1) {
      const isAjaxForm = form.classList.contains('ajax-submit') ||
                         form.hasAttribute('data-ajax') ||
                         form.querySelector('[data-ajax-submit]');
      if (!isAjaxForm) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        const formData = new FormData(form);
        formData.append('sections', SECTION_ID);
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: formData
        })
          .then(r => r.json())
          .then(data => {
            // If sections came back, use them directly (single round-trip)
            if (data && data.sections && data.sections[SECTION_ID]) {
              swapDrawerHTML(data.sections[SECTION_ID]);
              if (!isOpen()) openDrawer();
              isAdding = false;
            } else {
              handleCartAddSuccess();
            }
          })
          .catch(err => {
            console.error('[ISJ Drawer] cart/add via fetch failed', err);
            form.submit();
          });
        return false;
      }
    }
  }, true);

  // ---------- Intercept native fetch for any other cart/add calls ----------
  const originalFetch = window.fetch;
  window.fetch = function() {
    const args = arguments;
    const url = args[0];
    const urlStr = typeof url === 'string' ? url : (url && url.url) || '';
    const init = args[1] || {};
    const method = (init.method || (typeof url === 'object' && url.method) || 'GET').toUpperCase();
    const isCartAdd = method === 'POST' && /\/cart\/add(\.js|\?|$)/.test(urlStr);

    const promise = originalFetch.apply(this, args);

    if (isCartAdd) {
      promise.then(response => {
        if (response && response.ok) handleCartAddSuccess();
      }).catch(() => {});
    }
    return promise;
  };

  // ---------- Intercept XHR ----------
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function(method, url) {
    this.__isjMethod = method;
    this.__isjUrl = url;
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function() {
    const xhr = this;
    const isCartAdd = (xhr.__isjMethod || '').toUpperCase() === 'POST' &&
      typeof xhr.__isjUrl === 'string' &&
      /\/cart\/add(\.js|\?|$)/.test(xhr.__isjUrl);

    if (isCartAdd) {
      xhr.addEventListener('load', function() {
        if (xhr.status >= 200 && xhr.status < 300) handleCartAddSuccess();
      });
    }
    return xhrSend.apply(this, arguments);
  };

  // ---------- Common theme cart events ----------
  document.addEventListener('cart:added', handleCartAddSuccess);
  document.addEventListener('cart:refresh', handleCartAddSuccess);
  document.addEventListener('product:added', handleCartAddSuccess);
  document.addEventListener('ajaxProduct:added', handleCartAddSuccess);

  // ---------- Escape key closes drawer ----------
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && isOpen()) closeDrawer();
  });

  // ---------- Mutation observer keeps Turbo's drawer suppressed ----------
  const turboObs = new MutationObserver(() => {
    if (isOpen()) suppressTurbo();
  });
  function startTurboObs() {
    if (document.body) turboObs.observe(document.body, { childList: true, subtree: true });
    else setTimeout(startTurboObs, 50);
  }
  startTurboObs();

  // ---------- Public API ----------
  window.ISJCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    isOpen: isOpen,
    rebind: bindHandlers,
    swap: swapDrawerHTML
  };

  // ---------- Init ----------
  function init() {
    bindHandlers();
    if (window.location.search.indexOf('viewcart=true') !== -1) {
      openDrawer();
    }
    suppressTurbo();
    // Remove any free gift whose tier is no longer met (server-stamped on the root) on first render.
    removeExpiredGifts();
    // Sweep orphan gift packaging + engraving lines on page load.
    // Delay slightly so just-added engraving/gift items have time to settle their _for_line_key property.
    setTimeout(reconcileOrphanGifts, 2000);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();